import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { EMPTY } from '@/lib/format'

export type StatAccent = 'none' | 'navy' | 'gold' | 'danger' | 'success'

export interface StatCardProps {
  /** Etykieta wersalikowa, np. „SPRAWY W TOKU". */
  label: string
  /** Duża liczba (Spectral). `null`/`undefined` → „—" (brak danych z API). */
  value: ReactNode
  /** Dopisek przy liczbie, np. „spraw", „z 41 wpływu". */
  unit?: string
  /** Stopka oddzielona linią, np. „3 nowe w tym tygodniu". */
  footer?: ReactNode
  /** Kolorowy pasek po lewej (jak kafle na ekranie skrzynki wpływu). */
  accent?: StatAccent
  /**
   * 'label-top' — pulpit (etykieta wersalikowa nad liczbą),
   * 'value-top' — skrzynka wpływu (liczba, pod nią opis zwykłym tekstem).
   */
  layout?: 'label-top' | 'value-top'
  loading?: boolean
  onClick?: () => void
  className?: string
}

const ACCENTS: Record<StatAccent, string> = {
  none: '',
  navy: 'before:bg-navy-800',
  gold: 'before:bg-gold',
  danger: 'before:bg-danger',
  success: 'before:bg-success',
}

/** Kafel KPI z pulpitu: etykieta wersalikowa, duża liczba, podpis. */
export function StatCard({
  label,
  value,
  unit,
  footer,
  accent = 'none',
  layout = 'label-top',
  loading = false,
  onClick,
  className,
}: StatCardProps) {
  const missing = value === null || value === undefined || value === '' || value === EMPTY
  const shown = missing ? EMPTY : value
  const interactive = Boolean(onClick)

  return (
    <div
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      className={cn(
        'am-card relative flex flex-col px-5 pb-3.5 pt-4',
        accent !== 'none' &&
          'before:absolute before:left-0 before:top-0 before:h-full before:w-[3px] before:content-[""]',
        ACCENTS[accent],
        interactive && 'cursor-pointer transition-colors duration-fast hover:border-line focus-visible:am-focus focus:outline-none',
        className,
      )}
    >
      {layout === 'label-top' ? <span className="t-label">{label}</span> : null}
      <div className={cn('flex items-baseline gap-2', layout === 'label-top' && 'mt-2.5')}>
        {loading ? (
          <span className="my-1 block h-[26px] w-16 animate-pulse rounded bg-[#e9edf1]" />
        ) : (
          <span className={cn('t-stat', missing && 'text-[#c2ccd4]')}>{shown}</span>
        )}
        {unit ? <span className="text-small text-muted">{unit}</span> : null}
      </div>
      {layout === 'value-top' ? <span className="mt-1.5 text-base text-muted">{label}</span> : null}
      {footer !== undefined ? (
        <div className="mt-3.5 border-t border-line-soft pt-2.5 text-micro text-muted">{footer}</div>
      ) : null}
    </div>
  )
}

/** Siatka 4 kafli KPI z pulpitu (zwija się do 2 i 1 kolumny). */
export function StatGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4', className)}>{children}</div>
}
